// invoice.api.js
import axios from 'axios';

/**
 * Download invoice as PDF
 * @param {String} invoiceId - Invoice ID to download
 * @param {String} filename - Optional custom filename
 */
export async function downloadInvoice(
  invoiceId,
  filename = `invoice-${invoiceId}.pdf`,
) {
  try {
    const response = await axios.get(`/api/invoices/${invoiceId}/download`, {
      withCredentials: true,
      responseType: 'blob', // Important for file downloads
      headers: {
        Accept: 'application/pdf',
      },
    });

    // Check if response is a PDF blob or redirect
    const contentType = response.headers['content-type'];

    if (contentType && contentType.includes('application/pdf')) {
      // Download the PDF file
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/pdf' }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return { success: true, type: 'pdf' };
    } else if (
      response.data &&
      typeof response.data === 'string' &&
      response.data.includes('stripe.com')
    ) {
      // Handle Stripe redirect
      window.location.href = response.data;
      return { success: true, type: 'stripe' };
    }
  } catch (error) {
    // eslint-disable-next-line no-unused-vars

    if (error.response) {
      // Server responded with error status
      switch (error.response.status) {
        case 404:
          throw new Error('Invoice not found');
        case 403:
          throw new Error('Not authorized to download this invoice');
        case 500:
          throw new Error('Server error while downloading invoice');
        default:
          throw new Error(`Download failed: ${error.response.status}`);
      }
    } else if (error.request) {
      // Request was made but no response received
      throw new Error('No response from server. Please check your connection.');
    } else {
      // Something else happened
      throw new Error(`Download failed: ${error.message}`);
    }
  }
}

/**
 * Get invoice details
 * @param {String} invoiceId
 */
export async function getInvoice(invoiceId) {
  const response = await axios.get(`/api/invoices/${invoiceId}`, {
    withCredentials: true,
  });
  return response.data;
}

/**
 * Get invoices for user (guest or host)
 * @param {String} userId
 * @param {String} role - 'guest' or 'host'
 */
export async function getUserInvoices(userId, role = 'guest') {
  const response = await axios.get(
    `/api/invoices/user/${userId}?role=${role}`,
    {
      withCredentials: true,
    },
  );
  return response.data;
}

/**
 * Create a new invoice
 * @param {Object} invoiceData
 */
export async function createInvoice(invoiceData) {
  const response = await axios.post('/api/invoices', invoiceData, {
    withCredentials: true,
  });
  return response.data;
}

/**
 * Update invoice status
 * @param {String} invoiceId
 * @param {String} status
 */
export async function updateInvoiceStatus(invoiceId, status) {
  const response = await axios.put(
    `/api/invoices/${invoiceId}/status`,
    {
      status,
    },
    {
      withCredentials: true,
    },
  );
  return response.data;
}

/**
 * Get invoice by request ID
 * @param {String} requestId
 */
export async function getInvoiceByRequest(requestId) {
  const response = await axios.get(`/api/invoices/request/${requestId}`, {
    withCredentials: true,
  });
  return response.data;
}
